<script lang="ts" setup>
import type { BlockSerializedChild } from '#layers/block-engine/types/block';
import { useElementSize, useResizeObserver } from '@vueuse/core';
import { computed, provide, reactive, shallowRef, useId, useTemplateRef, watch } from 'vue';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { getBlockConfig, useBlockConfigApi } from '#layers/block-engine/utils/block/api/config';
import { blockChildKey } from '#layers/block-engine/utils/block/api/context';
import { useHorizontalLayout } from '#layers/block-engine/utils/block/api/helpers';
import { blockPreviewIdPrefix, slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { getBlockInitialProps } from '#layers/block-engine/utils/block/default-props';
import { isBlockDefinition } from '#layers/block-engine/utils/block/editor';
import { useTranslationUtils } from '#layers/i18n/public';

const { child, slotName, index } = defineProps<{
	child?: BlockSerializedChild;
	slotName?: string;
	index: number;
}>();

const { insertion, bounds } = useBlockEditorContext();
const { getBlock } = useBlockLibraryStore();
const { translateString } = useTranslationUtils();

const targetId = computed(() => `add-${child?.id}-${slotName}-${index}`);

// Direction

const horizontalLayout = useHorizontalLayout();

// Preview animation

const previewEl = useTemplateRef('previewEl');
const previewContainer = useTemplateRef('previewContainer');
const previewSize = useElementSize(previewEl);
const previewSizeValues = reactive({
	width: 0,
	height: 0,
});

// Animation can move siblings without resizing their roots or the canvas.
useResizeObserver(previewContainer, () => {
	bounds.updateEvent.trigger(null);
}, { box: 'border-box' });

// Preview

const selected = computed(() => insertion.target.value?.id === targetId.value);

const previewBlock = shallowRef<{
	blockId: string;
	props: Record<string, any>;
} | null>(null);

const blockConfigApiBase = useBlockConfigApi(() => child);

watch(() => [selected.value && insertion.blockId.value], async () => {
	if (selected.value && insertion.blockId.value) {
		if (insertion.blockId.value === slotOutletBlockId) {
			previewBlock.value = {
				blockId: slotOutletBlockId,
				props: {},
			};

			return;
		}

		const addedBlock = getBlock(insertion.blockId.value);

		if (addedBlock) {
			const addedProps: Record<string, any> = {};

			if (isBlockDefinition(addedBlock)) {
				Object.assign(addedProps, getBlockInitialProps({
					block: addedBlock,
					context: blockConfigApiBase,
					translateString,
				}));

				// onInserted hook
				const config = getBlockConfig(addedBlock);

				if (config?.onInserted) {
					await config.onInserted({
						...blockConfigApiBase,
						getProps: () => addedProps,
						setProps: (value) => {
							Object.assign(addedProps, value);
						},
					});
				}
			}

			previewBlock.value = {
				blockId: addedBlock.id,
				props: addedProps,
			};
		}
	}
});

// This must happen on the same frame the new block is loaded and is going to be rendered
// otherwise there is a flicker (one frame without the preview nor the new block)
insertion.clearEvent.on(() => {
	previewBlock.value = null;
	previewSizeValues.width = 0;
	previewSizeValues.height = 0;
	bounds.updateEvent.trigger(null);
});

watch(() => [previewSize.width.value, previewSize.height.value] as const, ([width, height]) => {
	if (previewBlock.value) {
		previewSizeValues.width = width;
		previewSizeValues.height = height;
	}
});

// Injections

const randomId = useId();

provide(blockChildKey, () => ({
	id: `${blockPreviewIdPrefix}-${randomId}`,
	blockId: previewBlock.value?.blockId ?? '',
	props: previewBlock.value?.props ?? {},
	slots: {},
}));
</script>

<template>
	<div
		v-if="previewBlock"
		ref="previewContainer"
		data-testid="builder-block-preview"
		class="overflow-hidden rounded-md bg-primary/5 transition-all delay-50 duration-slow ease-cubic-out"
		:style="{
			...horizontalLayout ? {
				width: `${previewSizeValues.width}px`,
			} : {
				height: `${previewSizeValues.height}px`,
			},
		}"
	>
		<div
			ref="previewEl"
			class="relative opacity-50"
			:class="[
				horizontalLayout ? 'h-full w-max' : 'h-max w-full',
			]"
		>
			<NuxtErrorBoundary
				:key="previewBlock.blockId"
			>
				<Transition
					enter-active-class="transition-opacity duration-fast delay-50"
					enter-from-class="opacity-0"
					leave-active-class="transition-opacity duration-fast"
					leave-to-class="opacity-0"
					appear
					mode="out-in"
					:duration="{
						enter: 200,
						leave: 150,
					}"
				>
					<div>
						<BuilderSlotOutlet
							v-if="previewBlock.blockId === slotOutletBlockId"
							:outlet="{ id: 'preview', slotName: 'default', props: {} }"
						/>
						<BlockRender
							v-else
							:key="previewBlock.blockId"
							:block="previewBlock.blockId"
							:props="previewBlock.props"
							unwrap-block-definition
							skip-error-boundary
						>
							<div class="size-full min-h-10 min-w-10" />
						</BlockRender>
					</div>
				</Transition>

				<template #error>
					<BuilderBlockStaticPreview
						:block-id="previewBlock.blockId"
					/>
				</template>
			</NuxtErrorBoundary>
		</div>
	</div>
</template>
